<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { ButtonGroupV2, ButtonV2, Text } from '@Shelly/shelly-design-system/index';
	import { format } from 'svelte-i18n';

	export let editType: 'blind_spots' | 'room_boundaries' = 'room_boundaries';
	export let editingMode: 'draw' | 'erase' = 'draw';
	export let hasChanges: boolean = false;
	export let saving: boolean = false;

	const dispatch = createEventDispatcher();

	type Variant = 'primary' | 'secondary';

	function modeVariants(
		edit: typeof editType,
		mode: typeof editingMode,
	): { draw: Variant; erase: Variant } {
		if (edit === 'room_boundaries') {
			return {
				draw: 'secondary',
				erase: mode === 'erase' ? 'primary' : 'secondary',
			};
		}
		return {
			draw: mode === 'draw' ? 'primary' : 'secondary',
			erase: mode === 'erase' ? 'primary' : 'secondary',
		};
	}

	$: title =
		editType === 'blind_spots'
			? $format('presence_room_blind_spots', { default: 'Blind spots' })
			: $format('presence_room_boundary', { default: 'Room boundary' });
	$: hint =
		editType === 'blind_spots'
			? $format('presence_room_blind_spots_hint', {
					default: 'Draw areas where detection should be ignored.',
				})
			: $format('presence_room_boundary_hint', {
					default: 'Draw the room boundary on the grid above.',
				});

	$: drawLabel =
		editType === 'blind_spots'
			? $format('presence_blind_spots_draw', { default: 'Add blind spot' })
			: $format('presence_room_boundary_draw', { default: 'Add room area' });

	$: eraseLabel =
		editType === 'blind_spots'
			? $format('presence_blind_spots_erase', { default: 'Remove blind spot' })
			: $format('presence_room_boundary_erase', { default: 'Remove room area' });

	$: modeGroupAriaLabel =
		editType === 'blind_spots'
			? $format('presence_draw_mode_aria_blind_spots', {
					default: 'Blind spots: add or remove area',
				})
			: $format('presence_draw_mode_aria_boundary', {
					default: 'Room boundary: add or remove area',
				});

	$: ({ draw: drawVariant, erase: eraseVariant } = modeVariants(editType, editingMode));
</script>

<div class="draw-sidebar">
	<div class="draw-sidebar__title">
		<span class="draw-sidebar__dot"></span>
		<Text styleType="body" element="span">{title}</Text>
	</div>

	<ButtonGroupV2 orientation="vertical" class="width-full" aria-label={modeGroupAriaLabel}>
		<ButtonV2
			width="full"
			size="m"
			class="mb-s"
			variant={eraseVariant}
			aria-pressed={editingMode === 'erase'}
			icon="fat fa-eraser"
			on:click={() => dispatch('modeChange', 'erase')}
		>
			{eraseLabel}
		</ButtonV2>
		<ButtonV2
			width="full"
			size="m"
			class="mb-s"
			variant={drawVariant}
			aria-pressed={editingMode === 'draw'}
			icon="fat fa-pencil"
			on:click={() => dispatch('modeChange', 'draw')}
		>
			{drawLabel}
		</ButtonV2>
	</ButtonGroupV2>

	<Text styleType="body-small" class="text-color-muted">{hint}</Text>

	<div class="draw-sidebar__actions">
		{#if hasChanges}
			<ButtonV2
				variant="secondary"
				size="m"
				on:click={() => dispatch('discard')}
				disabled={saving}
			>
				{$format('button_discard', { default: 'Discard' })}
			</ButtonV2>
			<ButtonV2
				aria-label="save"
				variant="primary"
				size="m"
				on:click={() => dispatch('save')}
				disabled={saving}
			>
				{#if saving}
					<i class="fas fa-spinner fa-spin" aria-hidden="true"></i>
					{$format('button_saving', { default: 'Saving…' })}
				{:else}
					{$format('button_save', { default: 'Save' })}
				{/if}
			</ButtonV2>
		{:else}
			<ButtonV2 variant="secondary" size="m" on:click={() => dispatch('return')}>
				{$format('button_return', { default: 'Return' })}
			</ButtonV2>
		{/if}
	</div>
</div>

<style>
	.draw-sidebar {
		padding: 12px 14px 8px;
		display: flex;
		flex-direction: column;
		gap: 14px;
	}

	.draw-sidebar__title {
		display: flex;
		align-items: center;
		gap: 10px;
		margin: 0;
	}

	.draw-sidebar__title :global(span) {
		font-weight: 600;
		color: #eaeaea;
	}

	.draw-sidebar__dot {
		width: 16px;
		height: 16px;
		border-radius: 50%;
		flex-shrink: 0;
		display: inline-block;
		background: #888;
	}

	.draw-sidebar__actions {
		display: flex;
		gap: 8px;
		justify-content: flex-end;
	}
</style>
